import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faGear, faPause, faPlay } from "@fortawesome/pro-regular-svg-icons";

import { CampaignDisplayStatus, CampaignSummary } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import { classNames } from "@/utils/classNames";
import { CampaignStatus } from "@/enum/campaign-status.enum";
import {
	useLazyGetListCampaignSummaryQuery,
	useUpdateCampaignStatusMutation,
} from "@/pages/Private/redux/list/list.api";

import { CampaignStatusPill } from "./CampaignOverview/components/CampaignStatusPill";
import { canPauseInBulk, canStartInBulk, countsTowardsBulkState } from "./campaignBulkEligibility";

/** A campaign sub-row is either a loaded summary or a transient loading placeholder. */
export type CampaignSubRow = CampaignSummary | { loading: true };

const isLoadingRow = (row: CampaignSubRow): row is { loading: true } => "loading" in row;

/** Minimal lean-list shape the summaries hook reads (campaignSummary is folded into the lean list). */
interface SummaryListLike {
	id: number;
	campaignSummary?: CampaignSummary[] | null;
}

/** Cadence for re-fetching summaries while a campaign is activating (mirrors the list-status poll). */
const CAMPAIGN_SUMMARY_POLL_MS = 5000;

/**
 * List ids whose cached summary still has a campaign mid-activation (LOADING) — these are polled
 * until the reschedule completes and the status settles to ACTIVE/PAUSED.
 */
export const pickLoadingListIds = (summariesByList: Record<number, CampaignSummary[]>): number[] =>
	Object.entries(summariesByList)
		.filter(([, summaries]) => summaries.some((c) => c.status === CampaignDisplayStatus.LOADING))
		.map(([listId]) => Number(listId));

/**
 * Per-list campaign summaries for the List Overview (expandable sub-rows + the Campaigns bulk
 * button). The summaries are folded into the lean list response (no per-list request); after a
 * toggle (or while a campaign is activating) the affected list is re-fetched via `refresh` and
 * overlaid on the lean data, so the badge/bulk button update without reloading the whole page.
 */
export const useListCampaignSummaries = (
	lists: SummaryListLike[],
	{ onStatusChangeFailed }: { onStatusChangeFailed?: (failedCount: number) => void } = {}
) => {
	const [trigger] = useLazyGetListCampaignSummaryQuery();
	// Held in a ref so a caller may pass an inline arrow without re-creating every callback below.
	const notifyFailure = useRef(onStatusChangeFailed);

	useEffect(() => {
		notifyFailure.current = onStatusChangeFailed;
	}, [onStatusChangeFailed]);
	// Freshly re-fetched summaries (post-toggle / mid-activation), overlaid on the lean payload.
	const [refreshed, setRefreshed] = useState<Record<number, CampaignSummary[]>>({});
	const [loadingByList, setLoadingByList] = useState<Record<number, boolean>>({});
	const [updateCampaignStatus] = useUpdateCampaignStatusMutation();
	const [bulkPending, setBulkPending] = useState<Set<number>>(new Set());

	// Seed from the lean list payload — campaignSummary travels with the list request.
	const leanByList = useMemo(() => {
		const map: Record<number, CampaignSummary[]> = {};

		for (const list of lists) {
			if (list.campaignSummary) {
				map[list.id] = list.campaignSummary;
			}
		}

		return map;
	}, [lists]);

	const getSummaries = useCallback(
		(listId: number): CampaignSummary[] | undefined => refreshed[listId] ?? leanByList[listId],
		[refreshed, leanByList]
	);

	const refresh = useCallback(
		async (listId: number) => {
			setLoadingByList((prev) => ({ ...prev, [listId]: true }));

			try {
				const res = await trigger({ listId }).unwrap();

				setRefreshed((prev) => ({ ...prev, [listId]: res.data }));
			} catch {
				// Surfaced by the global RTK Query error middleware.
			} finally {
				setLoadingByList((prev) => ({ ...prev, [listId]: false }));
			}
		},
		[trigger]
	);

	// While a campaign is activating (LOADING), poll its list's summary until the status settles, so
	// the badge/bulk button flip to ACTIVE on their own (covers both per-campaign and bulk activation).
	const loadingListIds = useMemo(
		() => pickLoadingListIds({ ...leanByList, ...refreshed }),
		[leanByList, refreshed]
	);

	useEffect(() => {
		if (loadingListIds.length === 0) {
			return;
		}

		const interval = setInterval(() => {
			loadingListIds.forEach((listId) => refresh(listId));
		}, CAMPAIGN_SUMMARY_POLL_MS);

		return () => clearInterval(interval);
	}, [loadingListIds, refresh]);

	/** Sub-rows for the Table: loaded summaries, or a single loading placeholder while fetching. */
	const getSubRows = useCallback(
		(listId: number): CampaignSubRow[] => {
			const summaries = getSummaries(listId);

			if (summaries) {
				return summaries;
			}

			return loadingByList[listId] ? [{ loading: true }] : [];
		},
		[getSummaries, loadingByList]
	);

	/** Pause/resume a single campaign from its sub-row pill, then refresh that list's summary. */
	const toggleCampaign = useCallback(
		async (listId: number, campaign: CampaignSummary) => {
			const status =
				campaign.status === CampaignDisplayStatus.ACTIVE
					? CampaignStatus.PAUSED
					: CampaignStatus.RUNNING;

			try {
				await updateCampaignStatus({
					id: listId,
					campaignUuid: campaign.campaignId,
					status,
				}).unwrap();
				await refresh(listId);
			} catch {
				// PREC-4129: nothing else tells the user. `rtkRejectQueryMiddleware` only redirects on
				// 401 and on rejected 403 *queries*; a rejected mutation passes through it silently, so
				// swallowing here left the pill snapping back to its old state with no explanation.
				notifyFailure.current?.(1);
			}
		},
		[updateCampaignStatus, refresh]
	);

	/**
	 * Bulk pause/activate: activate every runnable, non-active campaign (incl. individually paused),
	 * or pause every active one — each via the per-campaign endpoint. The list rollup is left alone.
	 *
	 * READY is in the activate set (PREC-4129). It means "valid, has leads, never started", and
	 * {@link computeCampaignBulkState} already counts it as runnable — so a list holding one reads
	 * "Paused" and offers "Activate all". Leaving READY out made that button a no-op: no request, no
	 * error, no message, the exact "lässt sich nicht aktivieren" a BlueOak user reported on
	 * 2026-09-10. A control must not display a state it refuses to act on. VALID (no leads yet) stays
	 * for the same reason; INVALID and ENDED are excluded from the chip entirely upstream.
	 */
	const bulkToggle = useCallback(
		async (listId: number, next: CampaignStatus) => {
			const targets = (getSummaries(listId) ?? []).filter((campaign) =>
				next === CampaignStatus.RUNNING
					? canStartInBulk(campaign.status)
					: canPauseInBulk(campaign.status)
			);

			if (targets.length === 0) {
				return;
			}

			setBulkPending((prev) => new Set(prev).add(listId));

			try {
				// allSettled, not all: one campaign failing must not abandon its siblings mid-way. But
				// the rejections are counted and reported (PREC-4129) — the previous
				// `.catch(() => undefined)` made a bulk action that changed nothing look identical to
				// one that worked.
				const results = await Promise.allSettled(
					targets.map((campaign) =>
						updateCampaignStatus({
							id: listId,
							campaignUuid: campaign.campaignId,
							status: next,
						}).unwrap()
					)
				);
				const failed = results.filter((result) => result.status === "rejected").length;

				if (failed > 0) {
					notifyFailure.current?.(failed);
				}

				await refresh(listId);
			} finally {
				setBulkPending((prev) => {
					const updated = new Set(prev);

					updated.delete(listId);

					return updated;
				});
			}
		},
		[getSummaries, updateCampaignStatus, refresh]
	);

	const isBulkPending = useCallback((listId: number) => bulkPending.has(listId), [bulkPending]);

	return { getSubRows, getSummaries, refresh, toggleCampaign, bulkToggle, isBulkPending };
};

/** Bulk run-state of a list's campaigns (mockup parity), or null when none can run. */
export type CampaignBulkState = "active" | "partial" | "paused";

/**
 * Derives the Campaigns-column bulk state from per-campaign summaries: only valid, non-ended
 * campaigns can run; `active` when all of them run, `paused` when none do, else `partial`.
 * Returns null when there are no runnable campaigns ("No valid campaigns").
 */
export const computeCampaignBulkState = (
	summaries: CampaignSummary[]
): CampaignBulkState | null => {
	const runnable = summaries.filter((c) => countsTowardsBulkState(c.status));

	if (runnable.length === 0) {
		return null;
	}

	const active = runnable.filter((c) => c.status === CampaignDisplayStatus.ACTIVE).length;

	if (active === 0) {
		return "paused";
	}

	return active === runnable.length ? "active" : "partial";
};

/** Per-campaign status badge sub-cell (clickable pause/resume for ACTIVE/PAUSED). */
export const CampaignStatusSubCell: FunctionComponent<{
	row: CampaignSubRow;
	disabled?: boolean;
	onToggle: (campaign: CampaignSummary) => void;
}> = ({ row, disabled, onToggle }) => {
	if (isLoadingRow(row)) {
		return <Spinner color="#4d66f5" />;
	}

	return (
		<CampaignStatusPill disabled={disabled} status={row.status} onToggle={() => onToggle(row)} />
	);
};

/** Campaign name sub-cell with an on-hover "Campaign settings" action. */
export const CampaignNameSubCell: FunctionComponent<{
	row: CampaignSubRow;
	onSettings: (campaign: CampaignSummary) => void;
}> = ({ row, onSettings }) => {
	const { t } = useTranslation();

	if (isLoadingRow(row)) {
		return <span className="pl-6 text-sm text-text-meta">{t("basics.wait")}</span>;
	}

	return (
		<div className="relative flex min-w-0 items-center pl-6">
			<span className="truncate font-medium text-text" title={row.name}>
				{row.name}
			</span>
			{/* Hover action overlaid on the right of the name (mirrors the list-row View leads / Settings). */}
			<div className="absolute right-0 top-1/2 hidden -translate-y-1/2 items-center gap-1.5 bg-gradient-to-r from-transparent to-background-light pl-6 group-hover/subrow:flex">
				<button
					className={ROW_ACTION_BUTTON}
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onSettings(row);
					}}
				>
					<Icon className="w-3 h-3" icon={faGear} />
					{t("list.campaignSettings")}
				</button>
			</div>
		</div>
	);
};

/** Shared styling for the hover row-action buttons (list name + campaign name overlays). */
export const ROW_ACTION_BUTTON =
	"inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-border-light bg-white px-2.5 py-1 text-xs font-semibold text-text-meta shadow-sm hover:bg-brand-white hover:text-text";

type MetricKind = "contacted" | "replied" | "positive";

/** One engagement metric sub-cell (value + percentage + label), mirroring the mockup. */
export const CampaignMetricSubCell: FunctionComponent<{
	row: CampaignSubRow;
	metric: MetricKind;
}> = ({ row, metric }) => {
	const { t } = useTranslation();

	if (isLoadingRow(row)) {
		return <span />;
	}

	const value = row[metric];
	// Contacted is a share of all assigned leads; replies/positives are a share of contacted leads.
	const denominator = metric === "contacted" ? row.total : row.contacted;
	const pct = denominator > 0 ? Math.round((value / denominator) * 100) : 0;

	return (
		<div className="flex items-baseline gap-1.5">
			<span className={classNames("whitespace-nowrap tabular-nums text-text")}>
				<b className="font-bold">{value}</b>
				{metric === "contacted" ? <span className="text-text-meta"> / {row.total}</span> : null}
				<span className="text-text-meta"> ({pct}%)</span>
			</span>
			<span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-text-meta">
				{t(`list.campaignMetrics.${metric}`)}
			</span>
		</div>
	);
};

const BULK_BUTTON_BASE =
	"group/bulk inline-flex min-w-[140px] items-center justify-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors cursor-pointer";

const BULK_VARIANT: Record<CampaignBulkState, { classes: string; dot: string }> = {
	active: {
		classes:
			"bg-status-success-light text-status-success border-status-success hover:bg-gray-100 hover:text-gray-600 hover:border-gray-300",
		dot: "bg-status-success",
	},
	partial: {
		classes:
			"bg-status-warning-light text-status-warning border-status-warning hover:bg-status-success-light hover:text-status-success hover:border-status-success",
		dot: "bg-status-warning",
	},
	paused: {
		classes:
			"bg-gray-100 text-gray-600 border-gray-300 hover:bg-status-success-light hover:text-status-success hover:border-status-success",
		dot: "bg-gray-400",
	},
};

/**
 * Campaigns-column bulk control: shows the list's aggregate campaign state and, on hover, the bulk
 * action — "Pause all" for an active list, "Activate all" otherwise. Mirrors the per-campaign pill.
 */
export const CampaignBulkButton: FunctionComponent<{
	state: CampaignBulkState;
	onToggleAll: (next: CampaignStatus) => void;
}> = ({ state, onToggleAll }) => {
	const { t } = useTranslation();
	const variant = BULK_VARIANT[state];
	const isActive = state === "active";
	const label =
		state === "active"
			? t("list.campaignBulk.active")
			: state === "partial"
				? t("list.campaignBulk.partiallyActive")
				: t("list.campaignBulk.paused");

	return (
		<button
			className={classNames(BULK_BUTTON_BASE, variant.classes)}
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				onToggleAll(isActive ? CampaignStatus.PAUSED : CampaignStatus.RUNNING);
			}}
		>
			<span className="inline-flex items-center gap-1.5 group-hover/bulk:hidden">
				<span className={classNames("h-1.5 w-1.5 rounded-full", variant.dot)} />
				{label}
			</span>
			<span className="hidden items-center gap-1.5 group-hover/bulk:inline-flex">
				<Icon className="w-3 h-3" icon={isActive ? faPause : faPlay} />
				{isActive ? t("list.campaignBulk.pauseAll") : t("list.campaignBulk.activateAll")}
			</span>
		</button>
	);
};

/** "No valid campaigns" placeholder shown when a list has no runnable campaigns. */
const NoValidCampaigns: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<span className="inline-flex min-w-[140px] items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-text-disabled">
			<span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
			{t("basics.noActiveCampaigns")}
		</span>
	);
};

/**
 * Campaigns-column cell for a list row: a spinner while activating, "No valid campaigns" when there
 * are none, then the bulk Active/Partially active/Paused button once the summary has loaded.
 */
export const CampaignsBulkCell: FunctionComponent<{
	row: { campaignCount?: number | null; campaignStatus?: CampaignStatus | null };
	summaries: CampaignSummary[] | undefined;
	isUpdating: boolean;
	onToggleAll: (next: CampaignStatus) => void;
}> = ({ row, summaries, isUpdating, onToggleAll }) => {
	if (isUpdating || row.campaignStatus === CampaignStatus.LOADING) {
		return <Spinner color="#4d66f5" />;
	}

	if (!row.campaignCount) {
		return <NoValidCampaigns />;
	}

	if (!summaries) {
		return <Spinner color="#4d66f5" />;
	}

	const state = computeCampaignBulkState(summaries);

	return state ? (
		<CampaignBulkButton state={state} onToggleAll={onToggleAll} />
	) : (
		<NoValidCampaigns />
	);
};
